/**
 * Home, the inbox (wave W1 "The day starts in my inbox"; ruling 4, R05, R13).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * ⭐ WAVE EXPERIENCE A: Home is the account's PROJECTS AS CARDS first (Mahmoud's
 * doctrine), each carrying the server's own counts for that project (GET /api/me/home);
 * the blocks below them are the ones Home always had.
 *
 * Home is three things and nothing else: MY TASKS (what is addressed to me), MY QUEUES
 * (the records waiting on me, each counted by the same read its queue page lists) and
 * MY NEXT 14 DAYS. The analytics moved to the Dashboard, which stays its own page; the
 * identity block is gone from both (R05).
 *
 * A workflow task shows "Open the action", never "Complete": it closes by itself when its
 * act happens. Only an informational task can be marked done by hand, and if the server
 * still answers that a task closes through its action (409), the plain reason is shown
 * and the row turns into "Open the action".
 *
 * Built on the UI foundation (shadcn/ui parts + the TanStack DataTable), per
 * frontend/docs/UI_FOUNDATION_PILOT.md.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Toast } from "@/components/ui/toast";

import { FyiBlock } from "../components/FyiBlock";

import { listCalendarActivities, type CalendarActivity } from "../api/calendar";
import { fetchHomeCards, type HomeProjectCard } from "../api/home";
import { ApiError } from "../api/client";
import type { Me } from "../api/me";
import { readRfiQueue } from "../api/rfis";
import { completeTask, listTasks, type Task } from "../api/tasks";
import { TaskDue } from "../components/TaskDue";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { PERSONA_QUEUES, QUEUES, QUEUE_PREVIEW, type QueueDef, type QueueId } from "../nav/queues";
import { resolvePersonaShape, type PersonaShape } from "../nav/personaNav";
import {
  EmptyState,
  LoadingLine,
  NoAssignmentState,
  ShortNoticeChip,
  StateChip,
  day,
} from "./parts";
import { formatTaskAge, recordTasksPath, taskActionPath, taskTitle } from "./TasksPage";
import { projectFilterSearch } from "./projectFilter";
import { useQuery } from "./useQuery";
import "../brand/components.css";

export interface PersonaHomeProps {
  readonly me: Me;
}

/** How many open tasks Home reads; the Tasks page holds the rest. */
const HOME_TASK_LIMIT = 50;

function isoDay(offsetDays: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

/** The shape Home renders queues for. No class: no queues, never somebody else's. */
export function homeShape(me: Me): PersonaShape | null {
  if (me.workflowClass === null) {
    return null;
  }
  return resolvePersonaShape({ configurationAllowed: false, workflowClass: me.workflowClass });
}

/** A section of Home: its title, its one line of purpose, then its content. */
function HomeSection({
  title,
  lede,
  action,
  testId,
  children,
}: {
  readonly title: string;
  readonly lede: string;
  readonly action?: React.ReactNode;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3" data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
          <p className="text-sm text-muted-foreground">{lede}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Failed(): React.JSX.Element {
  return (
    <div className="q-refusal" role="alert">
      <p className="q-refusal__heading">{strings.home.loadFailed}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------------
// My projects (Wave Experience A, item 1)
// ---------------------------------------------------------------------------------

/**
 * One quality number on a card (wave b12 frontend item 12, Mahmoud's finding of
 * 2026-10-25). Every label takes the same two-line box, bottom-aligned, so the four
 * figures of a card sit on one baseline however a label wraps; and the figure is a link
 * to the register it counts, pre-filtered to the project.
 */
function CardFigure({
  label,
  value,
  to,
  project,
  testId,
}: {
  readonly label: string;
  readonly value: number;
  readonly to: string;
  readonly project: string;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <div className="grid min-w-0 grid-rows-[2rem_auto] gap-1" data-testid={testId}>
      <dt
        className="line-clamp-2 self-end text-xs leading-4 text-muted-foreground"
        data-testid={`${testId}-label`}
        title={label}
      >
        {label}
      </dt>
      <dd className="m-0 leading-6">
        <Link
          to={to}
          className="q-link text-base tabular-nums"
          data-testid={`${testId}-link`}
          aria-label={strings.projectFilter.figureLink(label, value, project)}
        >
          {String(value)}
        </Link>
      </dd>
    </div>
  );
}

/**
 * One project as Home draws it. Bright colour is for what needs attention (Mahmoud's
 * doctrine): only the "waiting on you" line carries the attention tone, and only when
 * the server counted something there.
 */
export function ProjectCard({ card }: { readonly card: HomeProjectCard }): React.JSX.Element {
  const waiting = card.actionableTaskCount > 0;
  const project = { id: card.projectId, humanId: card.humanId };
  return (
    <article
      className="@container flex min-w-0 flex-col gap-3 rounded-md border border-rule bg-card p-(--q-s0)"
      data-testid={`home-project-${card.humanId}`}
      data-attention={waiting ? "true" : "false"}
      style={waiting ? { borderColor: "var(--q-gold-soft)" } : undefined}
    >
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
        <Link
          to={`/portfolio/projects/${card.projectId}`}
          className="q-link min-w-0"
          data-testid="home-project-open"
          aria-label={strings.home.cardOpenBible(card.humanId)}
        >
          <span className="q-register__mono mr-2">{card.humanId}</span>
          <span className="text-foreground">{card.name}</span>
        </Link>
        <StateChip state={card.status} />
      </div>
      <div className="flex flex-col gap-1 text-sm">
        {waiting ? (
          <Link
            to={recordTasksPath({ type: "PROJECT", id: card.projectId }, card.humanId)}
            className="q-link font-medium"
            style={{ color: "var(--q-gold)" }}
            data-testid="home-project-waiting"
          >
            {strings.home.cardWaiting(card.actionableTaskCount)}
          </Link>
        ) : (
          <span className="text-muted-foreground" data-testid="home-project-waiting">
            {strings.home.cardNothingWaiting}
          </span>
        )}
        <span className="text-muted-foreground" data-testid="home-project-fyi">
          {strings.home.cardFyi(card.fyiCount)}
        </span>
        <span className="text-muted-foreground" data-testid="home-project-next">
          {card.nextActivity === null ? (
            strings.home.cardNoNext
          ) : (
            <>
              {strings.home.cardNext}: {day(card.nextActivity.plannedDate)}{" "}
              <Link to={`/activities/${card.nextActivity.id}`} className="q-link q-register__mono">
                {card.nextActivity.humanId}
              </Link>
            </>
          )}
        </span>
      </div>
      {/* Four across only when the card is wide enough for every label in two lines;
          a narrow card (three to a row on a laptop) takes them two by two. */}
      <dl
        className="m-0 grid grid-cols-2 gap-x-3 gap-y-2 @md:grid-cols-4"
        data-testid="home-project-quality"
      >
        <CardFigure
          label={strings.home.cardFindingsOpen}
          value={card.quality.findingsOpen}
          to={`/findings${projectFilterSearch(project, { view: "open" })}`}
          project={card.humanId}
          testId="home-project-findings"
        />
        <CardFigure
          label={strings.home.cardPunchOpen}
          value={card.quality.punchOpen}
          to={`/findings${projectFilterSearch(project, { view: "punch" })}`}
          project={card.humanId}
          testId="home-project-punch"
        />
        <CardFigure
          label={strings.home.cardInspectionsOpen}
          value={card.quality.inspectionsOpen}
          to={`/inspections${projectFilterSearch(project, { progress: "open" })}`}
          project={card.humanId}
          testId="home-project-inspections"
        />
        <CardFigure
          label={strings.home.cardRfisOpen}
          value={card.quality.rfisOpen}
          to={`/rfis${projectFilterSearch(project, { open: "1" })}`}
          project={card.humanId}
          testId="home-project-rfis"
        />
      </dl>
    </article>
  );
}

function MyProjects(): React.JSX.Element {
  const token = useToken();
  const cards = useQuery((signal) => fetchHomeCards(token, signal), [token]);
  // The cards that wait on the caller lead; the order within each group is the register's.
  const ordered = useMemo(
    () =>
      cards.kind === "ready"
        ? [...cards.data.items].sort(
            (a, b) => Number(b.actionableTaskCount > 0) - Number(a.actionableTaskCount > 0),
          )
        : [],
    [cards],
  );
  return (
    <HomeSection
      title={strings.home.projectsHeading}
      lede={strings.home.projectsLede}
      testId="home-projects"
    >
      {cards.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(cards.kind === "failed" || cards.kind === "absence") && <Failed />}
      {cards.kind === "ready" &&
        (ordered.length === 0 ? (
          <EmptyState
            title={strings.home.projectsEmptyTitle}
            body={strings.home.projectsEmptyBody}
          />
        ) : (
          <div
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            data-testid="home-project-cards"
          >
            {ordered.map((card) => (
              <ProjectCard key={card.projectId} card={card} />
            ))}
          </div>
        ))}
    </HomeSection>
  );
}

// ---------------------------------------------------------------------------------
// My tasks
// ---------------------------------------------------------------------------------

/** How many tasks Home lists before it hands over to the Tasks page. */
const HOME_TASK_SHOWN = 10;

/**
 * One task as Home lists it: what it asks, how long it has waited, and its one button.
 * A list, not a wide table: on a 390 px phone the button must sit under the thumb, not
 * off the right edge of a scrolling register (the Tasks page is the register).
 */
function TaskItem({
  task,
  busyTaskId,
  onComplete,
}: {
  readonly task: Task;
  readonly busyTaskId: string | null;
  readonly onComplete: (task: Task) => void;
}): React.JSX.Element {
  return (
    <li
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-rule bg-card p-(--q-s0)"
      data-testid="home-task"
    >
      <div className="flex min-w-[14rem] flex-1 flex-col gap-1">
        <strong className="text-foreground">{taskTitle(task.kind)}</strong>
        <span className="text-sm">{task.message}</span>
        <span className="text-xs text-muted-foreground">
          {formatTaskAge(task.createdAt)}
          {task.addressedToParty ? ` · ${strings.tasks.addressedToParty}` : ""}
        </span>
        <TaskDue task={task} />
      </div>
      {task.completesThroughAction ? (
        <Button asChild className="w-full sm:w-auto">
          <Link
            to={taskActionPath(task)}
            data-testid="home-open-action"
            style={{ textDecoration: "none" }}
          >
            {strings.tasks.openAction}
          </Link>
        </Button>
      ) : (
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          data-testid="home-complete-task"
          disabled={busyTaskId !== null}
          onClick={() => {
            onComplete(task);
          }}
        >
          {busyTaskId === task.id ? strings.tasks.completing : strings.tasks.complete}
        </Button>
      )}
    </li>
  );
}

function MyTasks({
  onSettled,
}: {
  readonly onSettled: (open: number | null) => void;
}): React.JSX.Element {
  const token = useToken();
  const [overrides, setOverrides] = useState<Readonly<Record<string, "done" | "action">>>({});
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);

  const tasks = useQuery(
    (signal) => listTasks(token, { openOnly: true, limit: HOME_TASK_LIMIT, signal }),
    [token],
  );

  const rows = useMemo(
    () =>
      tasks.kind === "ready"
        ? tasks.data.items
            .filter((task) => overrides[task.id] !== "done")
            .map((task) =>
              overrides[task.id] === "action"
                ? { ...task, completesThroughAction: true, actionableByMe: true }
                : task,
            )
        : [],
    [tasks, overrides],
  );
  // Wave Experience A, item 3: the list is what waits on me; the rest is for my information.
  const actionable = useMemo(() => rows.filter((task) => task.actionableByMe), [rows]);
  const fyi = useMemo(() => rows.filter((task) => !task.actionableByMe), [rows]);
  // Review finding E (the Director's ruling, 2026-10-23): Home reads at most
  // HOME_TASK_LIMIT rows, so "N more" counts from the server's own total of the acts
  // (TaskList.actionable), never from the rows it happened to fetch. A row the server's
  // 409 turned into an act is already in the server's number.
  const actionableTotal =
    tasks.kind === "ready"
      ? Math.max(actionable.length, tasks.data.actionable ?? actionable.length)
      : 0;
  const doneCount = Object.values(overrides).filter((value) => value === "done").length;
  const openCount = tasks.kind === "ready" ? Math.max(0, tasks.data.open - doneCount) : null;

  // Home's "all caught up" line needs to know the task count once it is known.
  // "All caught up" counts what waits on me; a note for my information does not.
  const settled =
    tasks.kind === "loading" ? undefined : tasks.kind === "ready" ? actionable.length : null;
  useEffect(() => {
    if (settled !== undefined) {
      onSettled(settled);
    }
  }, [onSettled, settled]);

  const onComplete = useCallback(
    (task: Task): void => {
      if (busyTaskId !== null) {
        return;
      }
      setBusyTaskId(task.id);
      completeTask(token, task.id)
        .then(() => {
          setOverrides((prev) => ({ ...prev, [task.id]: "done" }));
          setToast({ message: strings.tasks.completed, tone: "info" });
        })
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 409) {
            // Closes only through its act: say so plainly and offer the act instead.
            setOverrides((prev) => ({ ...prev, [task.id]: "action" }));
            setToast({
              message: error.problem?.detail ?? strings.tasks.closesThroughAction,
              tone: "info",
            });
            return;
          }
          setToast({
            message:
              error instanceof ApiError && error.problem?.detail
                ? error.problem.detail
                : strings.tasks.completeFailed,
            tone: "danger",
          });
        })
        .finally(() => {
          setBusyTaskId(null);
        });
    },
    [busyTaskId, token],
  );

  return (
    <HomeSection
      title={strings.home.tasksHeading}
      lede={strings.home.tasksLede}
      testId="open-tasks-lead"
      action={
        <Link to="/tasks" className="q-link text-sm" data-testid="persona-view-all-tasks">
          {strings.home.allTasks(openCount ?? 0)}
        </Link>
      }
    >
      {toast !== null && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {tasks.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(tasks.kind === "failed" || tasks.kind === "absence") && <Failed />}
      {tasks.kind === "ready" &&
        (actionable.length === 0 ? (
          <EmptyState
            title={strings.home.tasksEmptyTitle}
            body={strings.home.tasksEmptyBody}
            assignmentAware={false}
          />
        ) : (
          <>
            <ul className="flex flex-col gap-2" data-testid="home-tasks-list">
              {actionable.slice(0, HOME_TASK_SHOWN).map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  busyTaskId={busyTaskId}
                  onComplete={onComplete}
                />
              ))}
            </ul>
            {actionableTotal > HOME_TASK_SHOWN && (
              <Link to="/tasks" className="q-link text-sm" data-testid="home-tasks-more">
                {strings.queues.more(actionableTotal - HOME_TASK_SHOWN)}
              </Link>
            )}
          </>
        ))}
      {tasks.kind === "ready" && (
        <FyiBlock count={fyi.length} testId="home-tasks-fyi">
          <ul className="flex flex-col gap-2" data-testid="home-tasks-fyi-list">
            {fyi.map((task) => (
              <TaskItem key={task.id} task={task} busyTaskId={busyTaskId} onComplete={onComplete} />
            ))}
          </ul>
        </FyiBlock>
      )}
    </HomeSection>
  );
}

// ---------------------------------------------------------------------------------
// My queues
// ---------------------------------------------------------------------------------

function QueueCard({
  queue,
  onSettled,
}: {
  readonly queue: QueueDef;
  readonly onSettled: (id: QueueId, total: number | null) => void;
}): React.JSX.Element {
  const token = useToken();
  const result = useQuery((signal) => queue.read(token, signal), [token, queue]);
  const watch = useQuery(
    (signal) => (queue.watch === undefined ? Promise.resolve(0) : queue.watch.read(token, signal)),
    [token, queue],
  );
  const settled =
    result.kind === "loading" ? undefined : result.kind === "ready" ? result.data.total : null;
  useEffect(() => {
    if (settled !== undefined) {
      onSettled(queue.id, settled);
    }
  }, [onSettled, queue.id, settled]);
  const watchTotal = watch.kind === "ready" ? watch.data : 0;

  return (
    <article
      className="flex min-w-0 flex-col gap-2 rounded-md border border-rule bg-card p-(--q-s0)"
      data-testid={`queue-${queue.id}`}
    >
      <h4 className="text-sm text-muted-foreground">{queue.label}</h4>
      {result.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(result.kind === "failed" || result.kind === "absence") && <Failed />}
      {result.kind === "ready" && (
        <>
          <p
            className="text-xl tabular-nums text-foreground"
            data-testid={`queue-count-${queue.id}`}
          >
            {String(result.data.total)}
          </p>
          {result.data.total === 0 ? (
            <p className="text-sm text-muted-foreground">{queue.empty}</p>
          ) : (
            <ul className="flex flex-col gap-1.5" data-testid={`queue-items-${queue.id}`}>
              {result.data.items.map((item) => (
                <li key={item.id} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <Link to={item.to} className="q-link q-register__mono shrink-0">
                    {item.humanId}
                  </Link>
                  <span className="min-w-0 flex-1 truncate text-sm" title={item.title}>
                    {item.title}
                  </span>
                  <StateChip state={item.state} />
                </li>
              ))}
              {result.data.total > result.data.items.length && (
                <li className="text-sm text-muted-foreground">
                  {strings.queues.more(result.data.total - result.data.items.length)}
                </li>
              )}
            </ul>
          )}
          <Link
            to={queue.route}
            className="q-link mt-auto text-sm"
            data-testid={`queue-open-${queue.id}`}
          >
            {strings.queues.open(result.data.total)}
          </Link>
          {queue.watch !== undefined && watchTotal > result.data.total && (
            <p className="text-xs text-muted-foreground" data-testid={`queue-watch-${queue.id}`}>
              {queue.watch.text(watchTotal)}{" "}
              <Link to={queue.watch.route} className="q-link">
                {queue.watch.link}
              </Link>
            </p>
          )}
        </>
      )}
    </article>
  );
}

function RfiQueueCard({
  me,
  onSettled,
}: {
  readonly me: Me;
  readonly onSettled: (id: string, total: number | null) => void;
}): React.JSX.Element {
  const token = useToken();
  const isContractor = me.workflowClass === "contractor";
  const result = useQuery((signal) => readRfiQueue(token, { signal }), [token]);

  // ⭐ RULING B (2026-10-23): "waiting on you" means the viewer's side moves next. The
  // card lists only the requests whose next move is this account's side, by the server's
  // own word (`waiting_on_my_side`); a request waiting on the other side is not on it.
  // The count, the sections and Home's "all caught up" read the same rows.
  const waiting = useMemo(
    () => (result.kind === "ready" ? result.data.items.filter((item) => item.waitingOnMySide) : []),
    [result],
  );

  const settled =
    result.kind === "loading" ? undefined : result.kind === "ready" ? waiting.length : null;
  useEffect(() => {
    if (settled !== undefined) {
      onSettled("rfis", settled);
    }
  }, [onSettled, settled]);

  const toReview = useMemo(
    () => waiting.filter((item) => item.workflowState === "SUBMITTED"),
    [waiting],
  );

  const assignedToMe = useMemo(
    () => waiting.filter((item) => item.workflowState !== "SUBMITTED"),
    [waiting],
  );

  return (
    <article
      className="flex min-w-0 flex-col gap-2 rounded-md border border-rule bg-card p-(--q-s0)"
      data-testid="queue-rfis"
    >
      <h4 className="text-sm text-muted-foreground">{strings.queues.rfiQueue.label}</h4>
      {result.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(result.kind === "failed" || result.kind === "absence") && <Failed />}
      {result.kind === "ready" && (
        <>
          <p className="text-xl tabular-nums text-foreground" data-testid="queue-count-rfis">
            {String(waiting.length)}
          </p>
          {waiting.length === 0 ? (
            <p className="text-sm text-muted-foreground">{strings.queues.rfiQueue.empty}</p>
          ) : isContractor ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                <span className="font-medium tracking-[0.08em] uppercase">
                  {strings.queues.rfiQueue.yourMove}
                </span>
                <span className="tabular-nums" data-testid="queue-count-rfis-your-move">
                  {String(waiting.length)}
                </span>
              </div>
              <ul className="flex flex-col gap-1.5" data-testid="queue-items-rfis">
                {waiting.slice(0, QUEUE_PREVIEW).map((item) => (
                  <li
                    key={item.id}
                    className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
                    data-testid="queue-rfi-entry"
                  >
                    <Link to={`/rfis/${item.id}`} className="q-link q-register__mono shrink-0">
                      {item.humanId}
                    </Link>
                    <span className="min-w-0 flex-1 truncate text-sm" title={item.scope}>
                      {item.scope}
                    </span>
                    <StateChip state={item.workflowState} />
                  </li>
                ))}
                {waiting.length > QUEUE_PREVIEW && (
                  <li className="text-sm text-muted-foreground">
                    {strings.queues.more(waiting.length - QUEUE_PREVIEW)}
                  </li>
                )}
              </ul>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5" data-testid="queue-rfis-to-review">
                <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                  <span className="font-medium tracking-[0.08em] uppercase">
                    {strings.queues.rfiQueue.toReview}
                  </span>
                  <span className="tabular-nums" data-testid="queue-count-rfis-to-review">
                    {String(toReview.length)}
                  </span>
                </div>
                {toReview.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {strings.queues.rfiQueue.toReviewEmpty}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1.5" data-testid="queue-items-rfis-review">
                    {toReview.slice(0, QUEUE_PREVIEW).map((item) => (
                      <li
                        key={item.id}
                        className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
                        data-testid="queue-rfi-entry"
                      >
                        <Link to={`/rfis/${item.id}`} className="q-link q-register__mono shrink-0">
                          {item.humanId}
                        </Link>
                        <span className="min-w-0 flex-1 truncate text-sm" title={item.scope}>
                          {item.scope}
                        </span>
                        <ShortNoticeChip rfi={item} />
                        <StateChip state={item.workflowState} />
                      </li>
                    ))}
                    {toReview.length > QUEUE_PREVIEW && (
                      <li className="text-sm text-muted-foreground">
                        {strings.queues.more(toReview.length - QUEUE_PREVIEW)}
                      </li>
                    )}
                  </ul>
                )}
              </div>

              <div className="flex flex-col gap-1.5" data-testid="queue-rfis-assigned">
                <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                  <span className="font-medium tracking-[0.08em] uppercase">
                    {strings.queues.rfiQueue.assignedToMe}
                  </span>
                  <span className="tabular-nums" data-testid="queue-count-rfis-assigned">
                    {String(assignedToMe.length)}
                  </span>
                </div>
                {assignedToMe.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {strings.queues.rfiQueue.assignedToMeEmpty}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1.5" data-testid="queue-items-rfis-assigned">
                    {assignedToMe.slice(0, QUEUE_PREVIEW).map((item) => (
                      <li
                        key={item.id}
                        className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
                        data-testid="queue-rfi-entry"
                      >
                        <Link to={`/rfis/${item.id}`} className="q-link q-register__mono shrink-0">
                          {item.humanId}
                        </Link>
                        <span className="min-w-0 flex-1 truncate text-sm" title={item.scope}>
                          {item.scope}
                        </span>
                        <ShortNoticeChip rfi={item} />
                        <StateChip state={item.workflowState} />
                      </li>
                    ))}
                    {assignedToMe.length > QUEUE_PREVIEW && (
                      <li className="text-sm text-muted-foreground">
                        {strings.queues.more(assignedToMe.length - QUEUE_PREVIEW)}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          )}
          <Link
            to="/rfis?queue=true"
            className="q-link mt-auto text-sm"
            data-testid="queue-open-rfis"
          >
            {strings.queues.open(result.data.total)}
          </Link>
        </>
      )}
    </article>
  );
}

function MyQueues({
  me,
  shape,
  onSettled,
}: {
  readonly me: Me;
  readonly shape: PersonaShape | null;
  readonly onSettled: (id: string, total: number | null) => void;
}): React.JSX.Element {
  const ids = shape === null ? [] : PERSONA_QUEUES[shape];
  const showRfiQueue = shape !== null && shape !== "management" && shape !== "tpi";
  return (
    <HomeSection
      title={strings.home.queuesHeading}
      lede={strings.home.queuesLede}
      testId="home-queues"
    >
      {ids.length === 0 && !showRfiQueue ? (
        <p className="text-sm text-muted-foreground">{strings.home.queuesNone}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {ids.map((id) => (
            <QueueCard key={id} queue={QUEUES[id]} onSettled={onSettled} />
          ))}
          {showRfiQueue && <RfiQueueCard me={me} onSettled={onSettled} />}
        </div>
      )}
    </HomeSection>
  );
}

// ---------------------------------------------------------------------------------
// My next 14 days
// ---------------------------------------------------------------------------------

/** How many planned dates Home lists before it hands over to the calendar. */
const HOME_DATES_SHOWN = 8;

/** One planned date as Home lists it; wraps to two lines on a phone. */
function DateItem({ row }: { readonly row: CalendarActivity }): React.JSX.Element {
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-rule py-2 last:border-b-0"
      data-testid="home-date"
    >
      <span className="q-register__mono w-[6.5rem] shrink-0 text-sm">{day(row.plannedDate)}</span>
      <Link to={`/activities/${row.id}`} className="q-link q-register__mono shrink-0 text-sm">
        {row.humanId}
      </Link>
      <span className="min-w-[10rem] flex-1 text-sm">
        {row.verificationTypeLabel}
        <span className="text-muted-foreground"> · {row.contractorPartyName}</span>
      </span>
      <StateChip state={row.status} label={row.statusLabel} />
    </li>
  );
}

function MyNext14Days({ shape }: { readonly shape: PersonaShape | null }): React.JSX.Element {
  const token = useToken();
  // The inspector reads his OWN dates; everyone else reads the dates the server lets
  // them see (the third party's are its agency's, as its witness docket reads them).
  const mine = shape === "inspector";
  const feed = useQuery(
    (signal) =>
      listCalendarActivities(token, { fromDate: isoDay(0), toDate: isoDay(13), mine, signal }),
    [token, mine],
  );
  const toDocket = shape === "tpi";
  return (
    <HomeSection
      title={strings.home.next14Heading}
      lede={strings.home.next14Lede}
      testId="home-next14"
      action={
        <Link
          to={toDocket ? "/docket" : "/calendar"}
          className="q-link text-sm"
          data-testid="home-next14-open"
        >
          {toDocket ? strings.home.openDocket : strings.home.openCalendar}
        </Link>
      }
    >
      {feed.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {(feed.kind === "failed" || feed.kind === "absence") && <Failed />}
      {feed.kind === "ready" &&
        (feed.data.items.length === 0 ? (
          <EmptyState title={strings.home.next14EmptyTitle} body={strings.home.next14EmptyBody} />
        ) : (
          <>
            <ul className="flex flex-col" data-testid="home-dates-list">
              {[...feed.data.items]
                .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate))
                .slice(0, HOME_DATES_SHOWN)
                .map((row) => (
                  <DateItem key={row.id} row={row} />
                ))}
            </ul>
            {/* Finding E: the server's total, not the rows of one page. */}
            {feed.data.total > HOME_DATES_SHOWN && (
              <p className="text-sm text-muted-foreground" data-testid="home-dates-more">
                {strings.queues.more(feed.data.total - HOME_DATES_SHOWN)}
              </p>
            )}
          </>
        ))}
    </HomeSection>
  );
}

// ---------------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------------

export function PersonaHome({ me }: PersonaHomeProps): React.JSX.Element {
  const shape = homeShape(me);
  const queueIds = shape === null ? [] : PERSONA_QUEUES[shape];
  const showRfiQueue = shape !== null && shape !== "management" && shape !== "tpi";
  const [openTasks, setOpenTasks] = useState<number | null | undefined>(undefined);
  const [queueTotals, setQueueTotals] = useState<Readonly<Record<string, number | null>>>({});

  const onTasksSettled = useCallback((open: number | null) => {
    setOpenTasks((prev) => (prev === open ? prev : open));
  }, []);
  const onQueueSettled = useCallback((id: string, total: number | null) => {
    setQueueTotals((prev) => (prev[id] === total ? prev : { ...prev, [id]: total }));
  }, []);

  // Wave S: a counterparty account with no contract assigned reads nothing of its party;
  // its Home says why, once, instead of a row of empty queues and "all caught up".
  const unassigned = me.assignedContractCount === 0;

  // "All caught up" only when every part has answered, and answered zero.
  const caughtUp =
    !unassigned &&
    openTasks === 0 &&
    queueIds.every((id) => queueTotals[id] === 0) &&
    (showRfiQueue ? queueTotals.rfis === 0 : true);

  return (
    <div className="q-persona-home" data-testid="persona-home">
      <div className="q-ui mb-(--q-s1)">
        <h2 className="q-shell__heading" data-testid="greeting">
          {strings.shell.greeting(me.name)}
        </h2>
        <p className="q-shell__lede">{strings.home.lede}</p>
        {caughtUp && (
          <p className="text-sm text-muted-foreground" data-testid="home-caught-up">
            {strings.home.caughtUp}
          </p>
        )}
      </div>

      {unassigned && <NoAssignmentState testId="home-no-assignment" />}
      {!unassigned && <MyProjects />}
      <MyTasks onSettled={onTasksSettled} />
      {!unassigned && <MyQueues me={me} shape={shape} onSettled={onQueueSettled} />}
      {!unassigned && <MyNext14Days shape={shape} />}
    </div>
  );
}
